// src/pages/submit-app/MySubmissions.tsx
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { getAppsBySubmitter } from '../../services/appRegistryService';
import type { AppRegistryEntry, AppStatus } from '../../types/appRegistry';
import { APP_CATEGORY_LABELS } from '../../types/appRegistry';

const STATUS_STYLES: Record<AppStatus, { label: string; className: string }> = {
  pending: { label: 'Pending Review', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
  approved: { label: 'Approved', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
  changes_requested: { label: 'Changes Requested', className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' },
};

export default function MySubmissions() {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState<AppRegistryEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.email) return;
    setLoading(true);
    getAppsBySubmitter(user.email)
      .then(setSubmissions)
      .catch((err) => console.error('Failed to load submissions:', err))
      .finally(() => setLoading(false));
  }, [user?.email]);

  if (loading) {
    return <p className="text-gray-500 dark:text-gray-400">Loading submissions...</p>;
  }

  if (submissions.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500 dark:text-gray-400">
          You haven't submitted any apps yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {submissions.map((app) => {
        const statusInfo = STATUS_STYLES[app.status];
        return (
          <Card key={app.id}>
            <CardContent className="py-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {app.faviconUrl && (
                    <img
                      src={app.faviconUrl}
                      alt=""
                      className="h-8 w-8 rounded"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  )}
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">{app.name}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{app.url}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {APP_CATEGORY_LABELS[app.category]}
                    </p>
                  </div>
                </div>
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusInfo.className}`}>
                  {statusInfo.label}
                </span>
              </div>
              {app.status === 'changes_requested' && app.adminNotes && (
                <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-md">
                  <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                    Admin Feedback:
                  </p>
                  <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                    {app.adminNotes}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
